/**
 * SIGN IN — /login
 *
 * Email + password, then — when the account has two-step verification on —
 * a 6-digit code from the authenticator app or WhatsApp (or a backup code).
 * The form area keeps a fixed height so nothing jumps between the steps. The
 * left panel shows what the product does; it is always dark (the brand's
 * gold-on-charcoal), the form side follows the theme.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import type { AxiosError } from 'axios';
import {
  ArrowLeft, ArrowRight, AtSign, Camera, CheckCircle, Eye, EyeOff, KeyRound, LifeBuoy, MessageCircle, Music2, ShieldCheck, Smartphone,
  TriangleAlert,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandMark } from '@/components/brand/BrandMark';
import { authService, TwoFactorChallenge } from '@/services/auth.service';

const OTP_LENGTH = 6;
const messageOf = (error: unknown) => (error as AxiosError<{ message?: string }>)?.response?.data?.message;

export default function Login() {
  const { loginWithPassword, completeTwoFactor } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSuccess = () => {
    setSuccess(true);
    setTimeout(() => nav('/dashboard', { replace: true }), 420);
  };

  const submitPassword = async () => {
    const next: typeof errors = {};
    if (!email.trim()) next.email = 'Enter your work email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "That doesn't look like an email address.";
    if (!password) next.password = 'Enter your password.';
    setErrors(next);
    if (next.email) { emailRef.current?.focus(); return; }
    if (next.password) { passwordRef.current?.focus(); return; }
    setBusy(true);
    try {
      const pending = await loginWithPassword(email.trim(), password);
      if (!pending) { handleSuccess(); return; }
      setChallenge(pending);
      setCode(''); setBackupCode(''); setUseBackup(false);
      setCodeError(pending.deliveryError ?? '');
      setCooldown(pending.resendAfterSeconds ?? 0);
    } catch { /* the axios interceptor already toasted */ }
    finally { setBusy(false); }
  };

  const verify = async (value: string) => {
    if (!challenge || busy) return;
    setBusy(true);
    setCodeError('');
    try {
      const { backupCodesRemaining } = await completeTwoFactor(challenge.challengeId, value);
      if (backupCodesRemaining !== undefined) toast(`Backup code used. ${backupCodesRemaining} left — make new ones in Settings → My profile when you run low.`, { icon: '🔑', duration: 7000 });
      handleSuccess();
    } catch (error) {
      const message = messageOf(error) ?? 'That code could not be checked. Try again.';
      setCodeError(message);
      setCode('');
      if (/Sign in again/.test(message)) setTimeout(() => backToPassword(message), 1200);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!challenge || cooldown > 0 || busy) return;
    setBusy(true);
    try {
      const r = await authService.resendTwoFactor(challenge.challengeId);
      setCooldown(r.resendAfterSeconds);
      setCodeError('');
      toast.success('New code sent');
    } catch (error) { setCodeError(messageOf(error) ?? 'A new code could not be sent.'); }
    finally { setBusy(false); }
  };

  const backToPassword = (message?: string) => {
    setChallenge(null); setCode(''); setBackupCode(''); setCodeError(''); setPassword('');
    if (message) setErrors({ password: message });
    setTimeout(() => passwordRef.current?.focus(), 50);
  };

  return (
    <div className="relative min-h-screen bg-bg">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <BrandPanel />

        {/* ── form side ── */}
        <div className="relative flex flex-col">
          <div className="flex items-center justify-between px-6 pt-5 sm:px-10">
            <div className="flex items-center gap-2.5 lg:invisible">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1A1A1A]"><BrandMark size={20} /></span>
              <span className="font-display text-[15px] font-bold tracking-tight text-text-primary">Slightor</span>
            </div>
            <ThemeToggle />
          </div>

          <div className="flex flex-1 items-center justify-center px-6 pb-10 pt-6 sm:px-10">
            <div className="w-full max-w-[400px]">
              {/* Fixed height: moving to the code step never moves the page. */}
              <div className="relative min-h-[430px]">
                <AnimatePresence mode="wait" initial={false}>
                  {!challenge ? (
                    <motion.form key="password" noValidate {...fade}
                      onSubmit={(e) => { e.preventDefault(); submitPassword(); }}>
                      <h1 className="font-display text-[30px] font-bold leading-tight tracking-tight text-text-primary">Welcome back</h1>
                      <p className="mb-7 mt-1.5 text-sm text-text-muted">Sign in to your Slightor workspace.</p>
                      <Field label="Work email" htmlFor="login-email" error={errors.email}>
                        <Input ref={emailRef} id="login-email" type="email" value={email} invalid={!!errors.email}
                          onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((x) => ({ ...x, email: undefined })); }}
                          placeholder="you@company.com" iconLeft={<AtSign size={15} />} autoComplete="email" autoFocus className="h-11" />
                      </Field>
                      <Field label="Password" htmlFor="login-password" error={errors.password}
                        aside={capsLock ? <span className="inline-flex items-center gap-1 text-2xs font-medium text-warning"><TriangleAlert size={11} /> Caps Lock is on</span> : undefined}>
                        <Input ref={passwordRef} id="login-password" type={showPwd ? 'text' : 'password'} value={password} invalid={!!errors.password}
                          onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((x) => ({ ...x, password: undefined })); }}
                          onKeyDown={(e) => setCapsLock(e.getModifierState('CapsLock'))} onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))}
                          placeholder="Your password" iconLeft={<KeyRound size={15} />} autoComplete="current-password" className="h-11"
                          iconRight={(
                            <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label={showPwd ? 'Hide password' : 'Show password'}
                              className="rounded p-0.5 text-text-muted transition-colors hover:text-text-primary">
                              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                          )} />
                      </Field>
                      <Button type="submit" size="lg" className="mt-2 w-full" loading={busy} disabled={busy} iconRight={<ArrowRight size={16} />}>
                        Sign in
                      </Button>
                      <p className="mt-5 text-center text-xs leading-relaxed text-text-muted">
                        Forgot your password? Ask your workspace admin to reset it.
                      </p>
                    </motion.form>
                  ) : (
                    <motion.form key="code" noValidate {...fade}
                      onSubmit={(e) => { e.preventDefault(); verify(useBackup ? backupCode : code); }}>
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
                        {challenge.method === 'whatsapp' ? <MessageCircle size={20} /> : <Smartphone size={20} />}
                      </span>
                      <h1 className="mt-4 font-display text-[26px] font-bold leading-tight tracking-tight text-text-primary">Two-step verification</h1>
                      <p className="mb-6 mt-1.5 text-sm leading-relaxed text-text-muted">
                        {useBackup
                          ? 'Enter one of the backup codes you saved when you turned this on. Each code works once.'
                          : challenge.method === 'whatsapp'
                            ? <>We sent a 6-digit code to your WhatsApp <span className="font-medium text-text-secondary">{challenge.destination}</span>.</>
                            : 'Open your authenticator app and enter the 6-digit code for Slightor.'}
                      </p>

                      {codeError && (
                        <div role="alert" className="mb-4 flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft px-3 py-2.5 text-xs text-danger">
                          <TriangleAlert size={14} className="mt-0.5 shrink-0" />{codeError}
                        </div>
                      )}

                      {useBackup ? (
                        <Field label="Backup code" htmlFor="login-backup">
                          <Input id="login-backup" value={backupCode} onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                            placeholder="XXXX-XXXX" autoComplete="off" autoFocus className="h-11 font-mono tracking-widest" iconLeft={<LifeBuoy size={15} />} />
                        </Field>
                      ) : (
                        <Field label="Verification code">
                          <OtpBoxes value={code} onChange={(v) => { setCode(v); if (codeError) setCodeError(''); }} onComplete={(v) => verify(v)} disabled={busy} />
                        </Field>
                      )}

                      <Button type="submit" size="lg" className="mt-2 w-full" loading={busy}
                        disabled={busy || (useBackup ? backupCode.replace(/[^A-Z0-9]/g, '').length !== 8 : code.length !== OTP_LENGTH)} iconRight={<ArrowRight size={16} />}>
                        Verify and sign in
                      </Button>

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <button type="button" onClick={() => backToPassword()} className="inline-flex items-center gap-1 font-medium text-text-secondary hover:text-text-primary">
                          <ArrowLeft size={12} /> Back
                        </button>
                        <div className="flex items-center gap-3">
                          {challenge.method === 'whatsapp' && !useBackup && (
                            cooldown > 0
                              ? <span className="text-text-muted">Resend in <span className="tabular-nums">0:{String(cooldown).padStart(2, '0')}</span></span>
                              : <button type="button" onClick={resend} disabled={busy} className="font-medium text-primary hover:underline disabled:opacity-50">Resend code</button>
                          )}
                          <button type="button" onClick={() => { setUseBackup((v) => !v); setCodeError(''); }} className="font-medium text-primary hover:underline">
                            {useBackup ? 'Use a verification code' : 'Use a backup code'}
                          </button>
                        </div>
                      </div>
                      <p className="mt-5 text-center text-2xs leading-relaxed text-text-muted">Lost your phone and your backup codes? Ask your workspace admin to reset two-step verification.</p>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>

              <p className="mt-2 flex items-center justify-center gap-1.5 text-2xs text-text-muted lg:hidden">
                <ShieldCheck size={12} /> Encrypted sign-in · © {new Date().getFullYear()} Mi Coders
              </p>
            </div>
          </div>

          <AnimatePresence>
            {success && (
              <motion.div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-bg/95 backdrop-blur-sm"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18 }}>
                <motion.div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success"
                  initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 22, delay: 0.06 }}>
                  <CheckCircle size={32} strokeWidth={2} />
                </motion.div>
                <motion.p className="text-sm font-semibold text-text-primary"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18, duration: 0.16 }}>
                  Signed in — taking you in…
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

const fade = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.16, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
};

// ── pieces ───────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; htmlFor?: string; aside?: React.ReactNode; error?: string; children: React.ReactNode }> = ({ label, htmlFor, aside, error, children }) => (
  <div className="mb-4">
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-text-secondary">{label}</label>
      {aside}
    </div>
    {children}
    {error && <p role="alert" className="mt-1.5 text-xs text-danger">{error}</p>}
  </div>
);

/** Full-width segmented control; the thumb slides between the two methods. Arrow keys switch. */

function OtpBoxes({ value, onChange, onComplete, disabled }: {
  value: string; onChange: (v: string) => void; onComplete: (v: string) => void; disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => { refs.current[Math.min(value.length, OTP_LENGTH - 1)]?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const put = (i: number, raw: string) => {
    const incoming = raw.replace(/\D/g, '');
    if (!incoming) return;
    const next = (value.slice(0, Math.min(i, value.length)) + incoming).slice(0, OTP_LENGTH);
    onChange(next);
    refs.current[Math.min(next.length, OTP_LENGTH - 1)]?.focus();
    if (next.length === OTP_LENGTH) onComplete(next);
  };
  const key = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const cut = value[i] ? i : Math.max(i - 1, 0);
      onChange(value.slice(0, cut));
      refs.current[cut]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) { e.preventDefault(); refs.current[i - 1]?.focus(); }
    else if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) { e.preventDefault(); refs.current[i + 1]?.focus(); }
  };

  return (
    <div className="grid grid-cols-6 gap-2" role="group" aria-label="One-time code">
      {Array.from({ length: OTP_LENGTH }, (_, i) => (
        <input key={i} ref={(el) => { refs.current[i] = el; }}
          value={value[i] ?? ''} disabled={disabled} inputMode="numeric" pattern="[0-9]*" maxLength={OTP_LENGTH}
          autoComplete={i === 0 ? 'one-time-code' : 'off'} aria-label={`Digit ${i + 1}`}
          onChange={(e) => put(i, e.target.value)} onKeyDown={(e) => key(i, e)} onFocus={(e) => e.currentTarget.select()}
          className={clsx(
            'h-12 w-full rounded-xl border bg-surface text-center font-mono text-lg font-semibold text-text-primary transition-colors',
            'focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft disabled:opacity-60',
            value[i] ? 'border-border-strong' : 'border-border',
          )} />
      ))}
    </div>
  );
}

// ── brand panel ──────────────────────────────────────────────────────────

const GoogleG = () => (
  <svg width="13" height="13" viewBox="0 0 48 48" aria-hidden>
    <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12c-.2 2-1.5 5-4.4 7l6.7 5.2c4-3.7 6.8-9.1 6.8-15.8z" />
    <path fill="#34A853" d="M24 46c6 0 11-2 14.3-5.4l-6.7-5.2c-1.8 1.3-4.3 2.2-7.6 2.2-5.9 0-10.8-3.9-12.6-9.2l-7 5.4C7.8 41.1 15.3 46 24 46z" />
    <path fill="#FBBC05" d="M11.4 28.4A13.6 13.6 0 0 1 10.7 24c0-1.5.3-3 .7-4.4l-7-5.4A22 22 0 0 0 2 24c0 3.5.9 6.9 2.4 9.8l7-5.4z" />
    <path fill="#EA4335" d="M24 10.2c4.2 0 7 1.8 8.6 3.3l6.3-6.1C35 3.9 30 2 24 2 15.3 2 7.8 6.9 4.4 14.2l7 5.4C13.2 14.1 18.1 10.2 24 10.2z" />
  </svg>
);

type Channel = 'whatsapp' | 'instagram' | 'tiktok' | 'google';
const CHANNEL: Record<Channel, { tile: string; icon: React.ReactNode; name: string }> = {
  whatsapp: { tile: 'bg-[#25D366]', icon: <MessageCircle size={13} className="text-white" />, name: 'WhatsApp' },
  instagram: { tile: 'bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af]', icon: <Camera size={13} className="text-white" />, name: 'Instagram' },
  tiktok: { tile: 'bg-black ring-1 ring-white/15', icon: <Music2 size={13} className="text-white" />, name: 'TikTok' },
  google: { tile: 'bg-white', icon: <GoogleG />, name: 'Google Ads' },
};

const INBOX: Array<{ ch: Channel; text: string; chip: string; tone: 'gold' | 'green' | 'plain'; when: string }> = [
  { ch: 'instagram', text: 'New lead from your Eid campaign', chip: 'AI qualified', tone: 'gold', when: '2m' },
  { ch: 'whatsapp', text: 'Can I book a table for Friday at 7?', chip: 'Bot replied', tone: 'green', when: '5m' },
  { ch: 'google', text: 'Call request — Dubai Marina branch', chip: 'Assigned', tone: 'plain', when: '12m' },
  { ch: 'tiktok', text: 'Price for the red abaya, please?', chip: 'New', tone: 'plain', when: '18m' },
];

/** Always-dark showcase: what the product does, drawn as a glimpse of the inbox and a broadcast. */
function BrandPanel() {
  return (
    <aside className="relative hidden overflow-hidden bg-[#121214] text-[#F4F1EB] lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14"
      style={{
        backgroundImage: 'radial-gradient(60% 45% at 0% 0%, rgba(200,165,91,0.26) 0%, transparent 62%), radial-gradient(50% 40% at 100% 100%, rgba(200,165,91,0.16) 0%, transparent 60%)',
      }}>
      <div aria-hidden className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)', backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 35% 45%, #000 30%, transparent 80%)', WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 35% 45%, #000 30%, transparent 80%)',
        }} />

      <div className="relative flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1A1A1A] ring-1 ring-white/10"><BrandMark size={24} glow /></span>
        <div className="leading-tight">
          <div className="font-display text-lg font-bold tracking-tight">Slightor</div>
          <div className="text-2xs uppercase tracking-[0.2em] text-[#9A958A]">Marketing suite</div>
        </div>
      </div>

      <div className="relative max-w-[520px]">
        <h2 className="font-display text-[42px] font-bold leading-[1.06] tracking-tightest xl:text-[46px]">
          Every channel your customers use.
          <span className="block bg-gold-gradient bg-clip-text text-transparent">One golden workspace.</span>
        </h2>
        <p className="mt-5 max-w-[460px] text-[15px] leading-relaxed text-[#B5AE9F]">
          WhatsApp broadcasts and AI replies, Facebook, Instagram, TikTok and Google Ads, every lead and every conversation — in one place,
          built for growing brands across the Gulf.
        </p>

        <div className="relative mt-9">
          {/* inbox glimpse */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-md">
            <div className="flex items-center justify-between px-1.5 pb-2.5 pt-0.5">
              <span className="text-xs font-semibold text-[#F4F1EB]">Conversation hub</span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-[#88B362]">
                <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-[#88B362]" /> Live
              </span>
            </div>
            <div className="space-y-1">
              {INBOX.map((row) => {
                const c = CHANNEL[row.ch];
                return (
                  <div key={row.text} className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-white/[0.04]">
                    <span className={clsx('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', c.tile)}>{c.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] text-[#F4F1EB]">{row.text}</div>
                      <div className="text-[10px] text-[#8A8170]">{c.name} · {row.when} ago</div>
                    </div>
                    <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                      row.tone === 'gold' ? 'bg-[#C8A55B]/20 text-[#E5C77D]' : row.tone === 'green' ? 'bg-[#25D366]/15 text-[#7fe0a6]' : 'bg-white/[0.07] text-[#B5AE9F]')}>
                      {row.chip}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* broadcast glimpse, overlapping the inbox */}
          <div className="absolute -bottom-9 -right-4 w-[228px] rounded-2xl border border-white/10 bg-[#1B1B1F]/95 p-3.5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)] xl:-right-8">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#25D366]"><MessageCircle size={12} className="text-white" /></span>
              <span className="truncate text-[11.5px] font-semibold">Eid offer broadcast</span>
            </div>
            <div className="mt-3 space-y-2">
              {[['Delivered', 'w-[94%]', 'bg-[#E5C77D]'], ['Read', 'w-[72%]', 'bg-[#C8A55B]'], ['Replied', 'w-[31%]', 'bg-[#9C7E3F]']].map(([label, w, bg]) => (
                <div key={label}>
                  <div className="mb-1 text-[10px] text-[#9A958A]">{label}</div>
                  <div className="h-1.5 rounded-full bg-white/[0.07]"><div className={clsx('h-1.5 rounded-full', w, bg)} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-16 flex items-center justify-between text-xs text-[#8A8170]">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} /> Encrypted sign-in · every workspace's data kept separate</span>
        <span>© {new Date().getFullYear()} Mi Coders</span>
      </div>
    </aside>
  );
}
